import {
	ActionListItem,
	BusyIndicator,
	DropOverActionList,
	FontIconName,
	ListItem,
	SvgIconValue
} from "client/Components";
import { contains } from "client/Data/searching";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts
} from "client/Hooks";
import {
	isNil,
	isNotNil,
	hasFlag,
	generateTrackingId,
	ensureNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { SharePointProvider } from "./provider";
import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent, TileScrollableHandle } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { SharePointAction } from "client/Api/Apps/EditApp/saveApp";
import {
	SharePointTeamSiteListItemViewModel
} from "client/Api/Workspace/LiveInformation/SharePoint/sharePointLiveInformation";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { getSharePointSiteCollectionDocumentsUrl } from "client/Features/Documents/routing";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type SharePointLiveTileContentProps = {
	provider: SharePointProvider;
	enabledActions: SharePointAction;
	onNavigate: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const SharePointLiveTileContent = (props: SharePointLiveTileContentProps): ReactElement => {
	const {
		provider,
		enabledActions,
		onNavigate,
		onOpenTileMenu
	} = props;

	const {
		icon,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [actions, setActions] = useState<readonly DetailsAction[] | null>(null);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const alertManager: AlertManager = useAlerts();

	const backHandler = useCallback(
		(): void => setActions(null),
		[]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[addAnalyticsEvent, onNavigate]
	);

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			{isNil(actions)
				? (
					<LiveTileContent.Header icon={icon}>
						<LiveTileContent.Title text={localize("_Sites")} />
					</LiveTileContent.Header>
				)
				: <LiveTileContent.BackHeader onClick={backHandler} />
			}
			<LiveTileContent.Content>
				<ListView
					provider={provider}
					isHidden={isNotNil(actions)}
					alertManager={alertManager}
					enabledActions={enabledActions}
					onAddAnalyticsEvent={addAnalyticsEvent}
				/>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
SharePointLiveTileContent.displayName = "SharePointLiveTileContent";

type ItemsLoadingState = { isLoading: true; }
	| { isLoading: false; } & (
		{
			hasError: false;
			items: readonly SharePointTeamSiteListItemViewModel[];
		}
		|
		{
			hasError: true;
		}
	);

type ListViewProps = {
	provider: SharePointProvider;
	isHidden: boolean;
	alertManager: AlertManager;
	enabledActions: SharePointAction;
	onAddAnalyticsEvent: () => void;
};

const ListView = (props: ListViewProps): ReactElement => {
	const {
		provider,
		isHidden,
		alertManager,
		enabledActions,
		onAddAnalyticsEvent
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [loadingState, setLoadingState] = useState<ItemsLoadingState>({ isLoading: true });

	const scrollableRef: RefObject<TileScrollableHandle | null> = useRef<TileScrollableHandle>(null);

	const { currentItems } = useDataSource(
		!loadingState.isLoading && !loadingState.hasError
			? loadingState.items
			: [],
		{
			searchTerm: searchTerm.value
		}
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm.value);

			scrollableRef.current
				?.scrollTo(0);
		},
		[searchTerm.value]
	);

	useEffect(
		(): (() => void) | void => {

			if (!provider.isAvailable) {
				return;
			}

			const loadList = async (signal: AbortSignal): Promise<void> => {
				setLoadingState({ isLoading: true });

				try {
					const items: readonly SharePointTeamSiteListItemViewModel[] = await provider.list();

					if (signal.aborted) {
						return;
					}

					setLoadingState({
						isLoading: false,
						hasError: false,
						items
					});

				} catch (e) {
					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			};

			const cancellation = new AbortController();
			loadList(cancellation.signal);
			return () => cancellation.abort();
		},
		[provider]
	);

	const renderContent = (): ReactElement => {
		if (!provider.isAvailable) {
			return <LiveTileContent.Message text={localize("_TheConnectionWithSharePointIsNotConfiguredThereforeWeCouldNotRetrieveYourSites")} />;
		}

		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasError) {
			return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
		}

		const hasAnyTeams: boolean = loadingState.items
			.length > 0;

		if (!hasAnyTeams) {
			return <LiveTileContent.Message text={localize("_YouDontHaveAnySites")} />;
		}

		if (currentItems.length === 0) {
			return <LiveTileContent.Message text={localize("_NoResultsFound")} />;
		}

		return (
			<LiveTileContent.Scrollable ref={scrollableRef}>
				<LiveTileContent.List>
					{currentItems.map((x) => (
						<SharePointListItem
							key={x.id}
							item={x}
							alertManager={alertManager}
							enabledActions={enabledActions}
							onAddAnalyticsEvent={onAddAnalyticsEvent}
						/>
					))}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<LiveTileContent.ContentStack isHidden={isHidden}>
			{provider.isAvailable && (
				<LiveTileContent.SearchInput
					value={searchTerm}
					onChange={(value, version) => setSearchTerm({ value, version })}
					trackingId="search-term"
					placeholder={localize("_Search")}
				/>
			)}
			<LiveTileContent.BusyIndicatorContainer>
				{renderContent()}
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
ListView.displayName = "ListView";

type SharePointListItemProps = {
	item: SharePointTeamSiteListItemViewModel;
	enabledActions: SharePointAction;
	alertManager: AlertManager;
	onAddAnalyticsEvent: () => void;
};

const SharePointListItem = (props: SharePointListItemProps): ReactElement => {
	const {
		item,
		enabledActions,
		alertManager,
		onAddAnalyticsEvent
	} = props;

	const {
		id,
		name,
		url
	} = item;

	const [areActionsOpen, setAreActionsOpen] = useState<boolean>(false);
	const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const trackingId: string = generateTrackingId("sharepoint-item", `|${id}`);

	const availableActions = useMemo(
		(): readonly DetailsAction[] => {
			const actions: DetailsAction[] = [];

			if (hasFlag(enabledActions, SharePointAction.OpenTeamSiteInSharePoint) && url) {
				actions.push({
					name: "open-in-share-point",
					text: localize("_OpenSiteInSharePoint"),
					icon: {
						kind: "svg",
						name: "ms-sharepoint"
					},
					execute: (): void => {
						navigateTo(url, true);
					}
				});
			}

			if (hasFlag(enabledActions, SharePointAction.OpenInDocumentsApp)) {
				actions.push({
					name: "open-documents",
					text: localize("_OpenDocuments"),
					icon: "folder-open",
					execute: (): void => {
						const documentsUrl: string = getSharePointSiteCollectionDocumentsUrl(id);
						navigateTo(documentsUrl, false);
					}
				});
			}

			return actions;
		},
		[
			enabledActions,
			id,
			url
		]
	);

	const openHandler = useCallback(
		(): void => {
			onAddAnalyticsEvent();

			if (availableActions.length === 0) {
				alertManager.showError(localize("_TheSiteCannotBeOpenedPleaseRefreshThePageAndTryAgain"));
			} else {
				availableActions[0].execute();
			}
		},
		[
			availableActions,
			alertManager,
			onAddAnalyticsEvent
		]
	);

	return (
		<ListItem
			key={id}
			trackingId={trackingId}
			noMobileBorderBottom
		>
			<ListItem.Content
				onClick={openHandler}
			>
				<ListItem.Title tooltip={name}>
					{name}
				</ListItem.Title>
			</ListItem.Content>

			{availableActions.length > 0 && (
				<ListItem.Action
					icon="ellipsis"
					onClick={() => setAreActionsOpen(isOpen => !isOpen)}
					trackingId="actions"
					bubbleClick={false}
					ref={buttonRef}
					isActive={areActionsOpen}
				/>
			)}

			{areActionsOpen && (
				<DropOverActionList
					target={ensureNotNil(buttonRef.current)}
					menuTitle={name}
					trackingId={generateTrackingId(trackingId, "__actions-dropover")}
					onClose={() => setAreActionsOpen(false)}
					targetAlignment="br"
					contentAlignment="tr"
				>
					{availableActions.map(
						(x): ReactElement => (
							<ActionListItem
								key={x.name}
								trackingId={x.name}
								text={x.text}
								icon={x.icon}
								onClick={() => {
									onAddAnalyticsEvent();
									x.execute();
								}}
							/>
						)
					)}
				</DropOverActionList>
			)}
		</ListItem>
	);
};
SharePointListItem.displayName = "SharePointListItem";

const useDataSource = createLocalDataSource<SharePointTeamSiteListItemViewModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.name, searchTerm)
		},
	})
	.getHook();

type DetailsAction = {
	name: string;
	text: string;
	icon: FontIconName | SvgIconValue;
	execute: () => void;
};
